import React, { useEffect, useState, useRef, useLayoutEffect, Component } from 'react';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { ChatDrawer } from '../components/ChatDrawer';
import { FloatingButtons } from '../components/FloatingButtons';
import { Link, useNavigate } from 'react-router-dom';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { ArrowLeft, ArrowRight, ArrowDown, Image, CheckCircle, MagnifyingGlassPlus } from '@phosphor-icons/react';
import { useScrollAnimation } from '../utils/AnimationObserver';
import '../styles/animations.css';
import { motion, useScroll, useTransform } from 'framer-motion';
// Vertical Timeline Component for FLEXVUI
function VerticalTimeline() {
  const timelineItems = [{
    number: '01',
    phase: 'Find',
    title: 'Problem Identification',
    desc: 'Identify right problems and select appropriate NLU platforms'
  }, {
    number: '02',
    phase: 'Listen',
    title: 'System Analysis',
    desc: 'Analyze existing systems and gather conversational data'
  }, {
    number: '03',
    phase: 'Empathize',
    title: 'User Research',
    desc: 'Conduct user interviews and map customer journeys'
  }, {
    number: '04',
    phase: 'eXtract',
    title: 'Data Parsing',
    desc: 'Parse data to identify intents and conversation patterns'
  }, {
    number: '05',
    phase: 'Version',
    title: 'Prototyping',
    desc: 'Build initial prototypes based on conversational maps'
  }, {
    number: '06',
    phase: 'Upskill',
    title: 'Testing & Refinement',
    desc: 'Test with users and refine based on training data'
  }, {
    number: '07',
    phase: 'Introduce',
    title: 'Launch & Validation',
    desc: 'Launch and validate with stakeholders'
  }];
  return <div className="relative py-12 ml-4 sm:ml-8" data-id="element-464">
      {/* Vertical Line */}
      <div className="absolute left-[11px] top-0 bottom-0 w-0.5 bg-gray-200" data-id="element-465" />

      <div className="space-y-24" data-id="element-466">
        {timelineItems.map((item, index) => <motion.div key={index} initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        delay: index * 0.2
      }} className="relative flex items-start gap-8" data-id="element-467">
            {/* Timeline Dot with Number */}
            <div className="relative z-10 flex-shrink-0 w-12 h-12 rounded-full bg-[#1967d2] ring-8 ring-white shadow-sm flex items-center justify-center mt-6" data-id="element-468">
              <span className="text-white font-bold text-sm" data-id="element-469">
                {item.number}
              </span>
            </div>

            {/* Content Card */}
            <div className="flex-1 bg-white p-8 rounded-2xl shadow-xl border border-gray-100 flex flex-col gap-4 group hover:-translate-y-2 transition-transform duration-300" data-id="element-470">
              <div className="w-12 h-1 bg-[#1967d2] mb-2" data-id="element-471" />
              <span className="text-xs font-bold text-slate-400 tracking-widest uppercase" data-id="element-472">
                {item.phase}
              </span>
              <h3 className="text-2xl font-medium text-[#1967d2]" data-id="element-473">
                {item.title}
              </h3>
              <p className="text-slate-600 leading-relaxed text-base" data-id="element-474">
                {item.desc}
              </p>
            </div>
          </motion.div>)}
      </div>
    </div>;
}
interface CaseStudyAIComparisonProps {
  isOverlay?: boolean;
}
export function CaseStudyAIComparison({
  isOverlay = false
}: CaseStudyAIComparisonProps) {
  const {
    viewPreference,
    isChatDrawerOpen,
    navigatedFromChat
  } = usePortfolio();
  const navigate = useNavigate();
  const cameFromChat = sessionStorage.getItem('cameFromChat') === 'true';
  const cameFromHome = sessionStorage.getItem('navigatedFromHome') === 'true';
  const showBackButton = cameFromChat || cameFromHome;
  const handleBackToHome = () => {
    sessionStorage.removeItem('cameFromChat');
    sessionStorage.removeItem('navigatedFromHome');
    navigate('/');
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };
  const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
  useCaseStudyNavigation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  const [activeSection, setActiveSection] = useState('overview');
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    // Observe all sections
    const sections = ['overview', 'challenge', 'approach', 'impact', 'documentation'];
    sections.forEach(sectionId => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });
    return () => {
      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 128; // Account for sticky header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  const handleCaseStudyClick = (slug: string) => {
    console.log('Case study navigation handled by ChatDrawer:', slug);
  };
  const navItems = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'challenge',
    label: 'Challenge'
  }, {
    id: 'approach',
    label: 'Approach'
  }, {
    id: 'impact',
    label: 'Impact'
  }, {
    id: 'documentation',
    label: 'Full Documentation'
  }];
  return <>
      {!isOverlay && <CustomCursor data-id="element-475" />}

      <div className="flex min-h-screen bg-white text-black font-switzer" data-id="element-476">
        <div className="flex-1 min-w-0 flex flex-col relative" data-id="element-477">
          {!isOverlay && <CaseStudyHeader data-id="element-478" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-479" />}

          {/* Hero Section */}
          <section className="pt-32 pb-12 px-4 sm:px-8 md:px-12 lg:px-24 max-w-[1400px] mx-auto w-full" data-id="element-480">
            <div className="max-w-4xl" data-id="element-481">
              {!isOverlay && <button onClick={handleBackToHome} className="flex items-center gap-2 text-gray-600 hover:text-[#1967d2] transition-colors mb-6 group" aria-label="Back to portfolio" style={{
              marginBottom: '28px'
            }} data-id="element-482">
                  <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-483" />
                  <span className="text-sm font-medium" data-id="element-484">Back to Portfolio</span>
                </button>}

              <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-8 text-[#1967d2] leading-[1.1]" data-id="element-485">
                Conversational AI
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl font-light" data-id="element-486">
                Agency project evaluating conversational AI platforms and
                building proof of concept for enterprise customer service
                automation.
              </p>
            </div>
          </section>

          {/* Full Width Hero Image */}
          <div className="w-full h-[60vh] sm:h-[80vh] overflow-hidden mb-24" data-id="element-487">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/d2sojXsLucASkJnUpLu1Ks/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png" alt="Project EVE Hero" className="w-full h-full object-cover object-center" data-id="element-488" />
          </div>

          {/* Main Content Grid */}
          <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-8 md:px-12 lg:px-24 pb-32" data-id="element-489">
            <div className="lg:grid lg:grid-cols-12 gap-12" data-id="element-490">
              {/* Sticky Navigation Sidebar */}
              <div className="hidden lg:block lg:col-span-3" data-id="element-491">
                <div className="sticky top-32 space-y-6" data-id="element-492">
                  {navItems.map(item => <button key={item.id} onClick={() => scrollToSection(item.id)} className={`block text-sm tracking-wide transition-colors duration-300 ${activeSection === item.id ? 'text-[#1967d2] font-medium border-l-2 border-[#1967d2] pl-4' : 'text-gray-400 hover:text-gray-600 pl-4 border-l-2 border-transparent'}`} data-id="element-493">
                      {item.label}
                    </button>)}
                </div>
              </div>

              {/* Content Column */}
              <div className="lg:col-span-9 space-y-32" data-id="element-494">
                {/* Overview Section */}
                <section id="overview" className="scroll-mt-32" data-id="element-495">
                  <div className="grid md:grid-cols-2 gap-16 mb-24" data-id="element-496">
                    <div data-id="element-497">
                      <h3 className="text-sm font-medium text-[#1967d2] uppercase tracking-wider mb-2" data-id="element-498">
                        Role
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-499">
                        Conversational Designer
                      </p>
                    </div>
                    <div data-id="element-500">
                      <h3 className="text-sm font-medium text-[#1967d2] uppercase tracking-wider mb-2" data-id="element-501">
                        Project Duration
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-502">3 Months</p>
                    </div>
                  </div>

                  <h2 className="text-3xl font-light mb-8 text-[#1967d2]" data-id="element-503">
                    Overview
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl" data-id="element-504">
                    A financial services client faced 100,000+ monthly calls
                    with limited automation. I evaluated conversational AI
                    platforms and built working prototypes to demonstrate
                    feasibility.
                  </p>

                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-505">
                    This was my first conversational design project. I taught
                    myself the discipline, created a methodology that gained
                    industry recognition, and delivered prototypes that secured
                    Phase 2 funding.
                  </p>

                  <div className="bg-gray-50 rounded-2xl p-8 md:p-12" data-id="element-506">
                    <h3 className="text-xl font-medium mb-8 text-[#1967d2]" data-id="element-507">
                      Key Achievements
                    </h3>
                    <div className="space-y-4" data-id="element-508">
                      {['Evaluated <span class="font-semibold text-slate-800">50+ platforms</span>, narrowing to 5 finalists', 'Created <span class="font-semibold text-slate-800">FLEXVUI methodology</span> adopted agency-wide', 'Built <span class="font-semibold text-slate-800">working prototypes in Watson and Dialogflow</span>', 'Discussed with <span class="font-semibold text-slate-800">Braden Ream (Voiceflow founder)</span>', 'Secured <span class="font-semibold text-slate-800">Phase 2 funding</span>'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-509">
                          <CheckCircle className="w-5 h-5 text-[#1967d2] mt-1 flex-shrink-0" data-id="element-510" />
                          <span className="text-slate-700 text-lg" dangerouslySetInnerHTML={{
                        __html: item
                      }} data-id="element-511" />
                        </div>)}
                    </div>
                  </div>
                </section>

                {/* Challenge Section - Merged Problem + Objectives */}
                <section id="challenge" className="scroll-mt-32" data-id="element-512">
                  <h2 className="text-3xl font-light mb-8 text-[#1967d2]" data-id="element-513">
                    Challenge
                  </h2>

                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-514">
                    The client's Innovation Lab partnered with our agency to
                    develop a proof of concept for conversational AI. Working
                    closely with their Director and Lead Innovation Manager, we
                    evaluated platforms and built prototypes to demonstrate
                    feasibility for their 100,000+ monthly call volume.
                  </p>

                  {/* Problem Statement */}
                  <div className="mb-16 border-l-2 border-[#1967d2] pl-8 py-4" data-id="element-515">
                    <h3 className="text-xs font-bold text-[#1967d2] uppercase tracking-wider mb-6 opacity-60" data-id="element-516">
                      Problem Statement
                    </h3>
                    <p className="text-2xl font-light leading-relaxed text-slate-900 mb-0" data-id="element-517">
                      A financial services client handling{' '}
                      <span className="font-medium text-[#1967d2]" data-id="element-518">
                        100,000+ monthly customer service calls
                      </span>{' '}
                      needed to evaluate conversational AI platforms for
                      automation, but lacked{' '}
                      <span className="font-medium text-[#1967d2]" data-id="element-519">
                        structured methodology, platform expertise, and proof of
                        concept
                      </span>{' '}
                      to validate technical feasibility and secure stakeholder
                      buy-in.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6" data-id="element-520">
                    {[{
                    title: 'Platform Selection',
                    desc: "Evaluate enterprise AI platforms for MoneyGram's scale"
                  }, {
                    title: 'Proof of Concept',
                    desc: 'Build working demos to validate technical approach'
                  }, {
                    title: 'Data Challenge',
                    desc: 'Parse 16,000+ unstructured customer messages'
                  }, {
                    title: 'Methodology Gap',
                    desc: 'No established conversational design process at agency'
                  }].map((card, idx) => <div key={idx} className="bg-gray-50 p-6 rounded-xl" data-id="element-521">
                        <h4 className="font-medium text-lg mb-2 text-[#1967d2]" data-id="element-522">
                          {card.title}
                        </h4>
                        <p className="text-slate-600" data-id="element-523">{card.desc}</p>
                      </div>)}
                  </div>
                </section>

                {/* Approach Section - Merged Solution + Process */}
                <section id="approach" className="scroll-mt-32" data-id="element-524">
                  <h2 className="text-3xl font-light mb-8 text-[#1967d2]" data-id="element-525">
                    Approach
                  </h2>

                  <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl" data-id="element-526">
                    I created the{' '}
                    <span className="font-semibold text-slate-900" data-id="element-527">
                      FLEXVUI methodology
                    </span>{' '}
                    - a seven-phase framework combining Google Design Sprint
                    principles with conversational AI best practices. This
                    became the foundation for all agency VUI projects.
                  </p>

                  {/* FLEXVUI Framework */}
                  <div className="mb-24" data-id="element-528">
                    <h3 className="text-xl font-medium mb-8 text-[#1967d2]" data-id="element-529">
                      FLEXVUI Framework
                    </h3>
                    <VerticalTimeline data-id="element-530" />
                  </div>
                </section>

                {/* Impact Section - Metrics + Brief Reflection */}
                <section id="impact" className="scroll-mt-32" data-id="element-531">
                  <h2 className="text-3xl font-light mb-12 text-[#1967d2]" data-id="element-532">
                    Impact
                  </h2>

                  <div className="grid md:grid-cols-2 gap-6 mb-16" data-id="element-533">
                    <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm hover:border-gray-300 transition-all duration-300" data-id="element-534">
                      <div className="text-4xl font-light text-[#1967d2] mb-4" data-id="element-535">
                        50+
                      </div>
                      <h3 className="text-xl font-medium text-[#1967d2] mb-4" data-id="element-536">
                        Platforms Evaluated
                      </h3>
                      <p className="text-slate-600" data-id="element-537">
                        Narrowed to 5 enterprise finalists
                      </p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm hover:border-gray-300 transition-all duration-300" data-id="element-538">
                      <div className="text-4xl font-light text-[#1967d2] mb-4" data-id="element-539">
                        2
                      </div>
                      <h3 className="text-xl font-medium text-[#1967d2] mb-4" data-id="element-540">
                        Working Prototypes
                      </h3>
                      <p className="text-slate-600" data-id="element-541">
                        Watson and Dialogflow demos
                      </p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm hover:border-gray-300 transition-all duration-300" data-id="element-542">
                      <h3 className="text-xl font-medium text-[#1967d2] mb-4" data-id="element-543">
                        Industry Recognition
                      </h3>
                      <p className="text-slate-600" data-id="element-544">
                        Discussed with Braden Ream (Voiceflow founder)
                      </p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm hover:border-gray-300 transition-all duration-300" data-id="element-545">
                      <h3 className="text-xl font-medium text-[#1967d2] mb-4" data-id="element-546">
                        Project Approved
                      </h3>
                      <p className="text-slate-600" data-id="element-547">Secured Phase 2 funding</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-8 md:p-12" data-id="element-548">
                    <h3 className="text-xl font-medium mb-6 text-[#1967d2]" data-id="element-549">
                      Key Takeaways
                    </h3>
                    <ul className="space-y-3 max-w-3xl" data-id="element-550">
                      <li className="flex items-start gap-3" data-id="element-551">
                        <CheckCircle className="w-5 h-5 text-[#1967d2] mt-1 flex-shrink-0" data-id="element-552" />
                        <span className="text-slate-700" data-id="element-553">
                          Self-directed learning enabled strategic contribution
                          from day one
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-554">
                        <CheckCircle className="w-5 h-5 text-[#1967d2] mt-1 flex-shrink-0" data-id="element-555" />
                        <span className="text-slate-700" data-id="element-556">
                          Creating frameworks delivers value beyond individual
                          projects
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-557">
                        <CheckCircle className="w-5 h-5 text-[#1967d2] mt-1 flex-shrink-0" data-id="element-558" />
                        <span className="text-slate-700" data-id="element-559">
                          Industry recognition validates approach and opens
                          broader conversations
                        </span>
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Full Documentation Section */}
                <section id="documentation" className="scroll-mt-32" data-id="element-560">
                  <h2 className="text-3xl font-light mb-8 text-[#1967d2]" data-id="element-561">
                    Full Project Documentation
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-562">
                    Explore complete project details, platform analysis, and
                    conversational flow designs.
                  </p>

                  {/* Full-width iframe with corner fullscreen button */}
                  <div className="relative w-full bg-gray-100 rounded-lg overflow-hidden border border-gray-200" data-id="element-563">
                    {/* Fullscreen button in top-right corner */}
                    <a href="https://gamma.app/embed/tpccpfnptxadvaw" target="_blank" rel="noopener noreferrer" className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white text-[#1967d2] px-4 py-2 rounded-full text-sm font-medium shadow-lg flex items-center gap-2 transition-all duration-300 hover:scale-105 backdrop-blur-sm" data-id="element-564">
                      <MagnifyingGlassPlus className="w-4 h-4" data-id="element-565" />
                      View Fullscreen
                    </a>

                    <div className="relative" style={{
                    paddingBottom: '64.29%'
                  }} data-id="element-566">
                      <iframe src="https://gamma.app/embed/tpccpfnptxadvaw" style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }} allow="fullscreen" title="Innovation Lab" data-id="element-567" />
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>

          {!isOverlay && <CaseStudyNavigation homePath={homePath} previousCase={{
          category: 'Case Studies',
          title: 'Cyber Security',
          slug: 'cyber-security'
        }} nextCase={{
          category: 'Featured Work',
          title: 'CWRU',
          slug: 'case-western'
        }} data-id="element-568" />}

          {!isOverlay && <Footer data-id="element-569" />}
        </div>

        {!isOverlay && <ChatDrawer onCaseStudyClick={handleCaseStudyClick} data-id="element-570" />}
      </div>

      {!isOverlay && !isChatDrawerOpen && <FloatingButtons data-id="element-571" />}
    </>;
}